<script setup lang="ts">
// 守狸 Web 身份登录入口（/login，P8 SSO 屏②统一登录 + 屏③零感兜底）。
// 真正的手机号/微信登录表单在 account 域统一登录页；本页只是 first-party 子域的品牌化入口：
//   - mount 先试一次零感进入（凭 account 会话 cookie 静默换 token），有会话直接放行、不展示登录卡；
//   - 无会话才落到品牌登录卡，点按钮跳 account 统一登录页走 PKCE。
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { sdk, isAuthed, accountHost, KEYTAIL_SCOPES } from '../lib/sdk'
import KeytailMascot from '../components/KeytailMascot.vue'

const route = useRoute()
const router = useRouter()

const error = ref('')
const busy = ref(false)
const checking = ref(true) // 首帧先尝试零感进入

const expired = route.query.reason === 'expired'

onMounted(async () => {
  // 已过期场景不再静默重试（会话多半已失效），直接展示登录卡。
  if (expired) {
    checking.value = false
    return
  }
  // main.ts 已在 mount 前 await sdk.bootstrap()；这里再核一次身份态，有会话直接进工作台。
  // 兜底再试一次 bootstrap（极少数 main 竞态 / 直接深链到 /login 的情况）。
  try {
    if (isAuthed() || (await sdk.bootstrap())) {
      router.replace('/')
      return
    }
  } catch {
    /* 静默失败 = 无会话，落到登录卡 */
  }
  checking.value = false
})

async function onLogin() {
  busy.value = true
  error.value = ''
  try {
    // startPKCE → location.assign，正常路径下函数永不 resolve（浏览器在跳转前进入 unload）。
    // 落到 catch 通常是 PKCE state 生成失败或 SDK 配错。
    const { url } = await sdk.startPKCE(KEYTAIL_SCOPES)
    window.location.assign(url)
  } catch (e) {
    error.value = (e as Error)?.message || '跳转登录失败，请重试'
    busy.value = false
  }
}
</script>

<template>
  <div class="kt-identity">
    <!-- 零感进入尝试中：极简过渡，不闪登录卡 -->
    <div v-if="checking" class="kt-id-card kt-id-card--slim">
      <span class="kt-dot" /> 正在进入守狸云…
    </div>

    <!-- 真首登：品牌化登录卡 -->
    <div v-else class="kt-id-card">
      <div class="kt-id-brand">
        <KeytailMascot :size="56" class="kt-id-mark" />
        <div class="kt-id-word">灵澜守狸</div>
        <div class="kt-id-tag">一个账号，点亮整个生态</div>
      </div>

      <div class="kt-id-prod">
        <b>即将进入 · 守狸 Web 端</b>
        <span>登录后用主密码解锁，浏览器内本地加解密管理账号与密钥</span>
      </div>

      <ul class="kt-id-points">
        <li><b>本地加密</b>密钥在你的浏览器里加解密，云端只存密文。</li>
        <li><b>统一身份</b>一个灵澜账号登录，全生态零感通用。</li>
        <li><b>会员上云</b>守狸云会员可跨设备安全同步你的保险箱。</li>
      </ul>

      <div v-if="expired" class="kt-id-hint kt-id-hint--warn">登录已过期，请重新登录。</div>
      <div v-if="error" class="kt-id-hint kt-id-hint--error">{{ error }}</div>

      <button class="kt-btn kt-btn--primary kt-id-btn" :disabled="busy" @click="onLogin">
        {{ busy ? '正在跳转灵澜账号…' : '使用灵澜账号登录' }}
      </button>

      <p class="kt-id-foot">
        授权范围：identity / keytail。<br />
        我们不保留你的账号密码——所有登录与授权由灵澜账号中心（{{ accountHost }}）完成。
      </p>
    </div>
  </div>
</template>
